import React from 'react';
import { Calendar, Shield, AlertCircle, FileText, Globe, Info, Users } from 'lucide-react';

const Disclaimer: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-dpn-pink/5 via-white to-dpn-olive/5">
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-dpn-raspberry/10 rounded-full">
              <AlertCircle className="h-12 w-12 text-dpn-raspberry" />
            </div>
          </div>
          <h1 className="text-4xl font-runtime font-bold text-gray-900 mb-4">
            Disclaimer
          </h1>
          <p className="text-lg text-gray-600 font-lucy-rose max-w-2xl mx-auto">
            Important legal disclaimers and limitations regarding the use of Durga Puja Network services.
          </p>
          <div className="mt-4 flex items-center justify-center text-sm text-gray-500">
            <Calendar className="h-4 w-4 mr-2" />
            <span>Last updated: 30.06.2025</span>
          </div>
        </div>

        {/* Disclaimer Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 space-y-8">
          
          {/* Website Disclaimer */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-raspberry/10 rounded-lg mr-3">
                <Globe className="h-5 w-5 text-dpn-raspberry" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">Website Disclaimer</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                The information provided by Mind's Workstation India Pvt. Ltd ("Company", "we", "our", "us") on www.durgapuja.network (the "Site") is for general informational purposes only. All information on the Site is provided in good faith, however we make no representation or warranty of any kind, express or implied, regarding the accuracy, adequacy, validity, reliability, availability, or completeness of any information on the Site.
              </p>
              <p>
                UNDER NO CIRCUMSTANCE SHALL WE HAVE ANY LIABILITY TO YOU FOR ANY LOSS OR DAMAGE OF ANY KIND INCURRED AS A RESULT OF THE USE OF THE SITE OR RELIANCE ON ANY INFORMATION PROVIDED ON THE SITE. YOUR USE OF THE SITE AND YOUR RELIANCE ON ANY INFORMATION ON THE SITE IS SOLELY AT YOUR OWN RISK.
              </p>
            </div>
          </section>

          {/* External Links Disclaimer */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-olive/10 rounded-lg mr-3">
                <Globe className="h-5 w-5 text-dpn-olive" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">External Links Disclaimer</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                The Site may contain (or you may be sent through the Site) links to other websites or content belonging to or originating from third parties or links to websites and features. Such external links are not investigated, monitored, or checked for accuracy, adequacy, validity, reliability, availability or completeness by us.
              </p>
              <p>
                WE DO NOT WARRANT, ENDORSE, GUARANTEE, OR ASSUME RESPONSIBILITY FOR THE ACCURACY OR RELIABILITY OF ANY INFORMATION OFFERED BY THIRD-PARTY WEBSITES LINKED THROUGH THE SITE OR ANY WEBSITE OR FEATURE LINKED IN ANY BANNER OR OTHER ADVERTISING. WE WILL NOT BE A PARTY TO OR IN ANY WAY BE RESPONSIBLE FOR MONITORING ANY TRANSACTION BETWEEN YOU AND THIRD-PARTY PROVIDERS OF PRODUCTS OR SERVICES.
              </p>
            </div>
          </section>

          {/* Testimonials Disclaimer */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-pink/10 rounded-lg mr-3">
                <Users className="h-5 w-5 text-dpn-pink" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">Testimonials Disclaimer</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                The Site may contain testimonials by users of our products and/or services. These testimonials reflect the real-life experiences and opinions of such users. However, the experiences are personal to those particular users, and may not necessarily be representative of all users of our products and/or services. We do not claim, and you should not assume that all users will have the same experiences.
              </p>
              <p className="font-semibold">
                YOUR INDIVIDUAL RESULTS MAY VARY.
              </p>
              <p>
                The testimonials on the Site are submitted in various forms such as text, audio and/or video, and are reviewed by us before being posted. They appear on the Site verbatim as given by the users, except for the correction of grammar or typing errors. Some testimonials may have been shortened for the sake of brevity, where the full testimonial contained extraneous information not relevant to the general public.
              </p>
              <p>
                The views and opinions contained in the testimonials belong solely to the individual user and do not reflect our views and opinions.
              </p>
            </div>
          </section>

          {/* Errors and Omissions Disclaimer */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-light-green/10 rounded-lg mr-3">
                <AlertCircle className="h-5 w-5 text-dpn-light-green" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">Errors and Omissions Disclaimer</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                While we have made every attempt to ensure that the information contained in this site has been obtained from reliable sources, Mind's Workstation India Pvt. Ltd is not responsible for any errors or omissions or for the results obtained from the use of this information. All information in this site is provided "as is", with no guarantee of completeness, accuracy, timeliness or of the results obtained from the use of this information, and without warranty of any kind, express or implied, including, but not limited to warranties of performance, merchantability, and fitness for a particular purpose.
              </p>
              <p>
                In no event will Mind's Workstation India Pvt Ltd, its related partnerships or corporations, or the partners, agents or employees thereof be liable to you or anyone else for any decision made or action taken in reliance on the information in this Site or for any consequential, special or similar damages, even if advised of the possibility of such damages.
              </p>
            </div>
          </section>

          {/* Logos and Trademarks Disclaimer */}
          <section>
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-raspberry/10 rounded-lg mr-3">
                <Shield className="h-5 w-5 text-dpn-raspberry" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">Logos and Trademarks Disclaimer</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                All logos and trademarks of third parties referenced on durgapuja.network are the trademarks and logos of their respective owners. Any inclusion of such trademarks or logos does not imply or constitute any approval, endorsement or sponsorship of Mind's Workstation India Pvt. Ltd by such owners.
              </p>
            </div>
          </section>

          {/* Contact Us */}
          <section className="border-t pt-8">
            <div className="flex items-center mb-4">
              <div className="p-2 bg-dpn-olive/10 rounded-lg mr-3">
                <Info className="h-5 w-5 text-dpn-olive" />
              </div>
              <h2 className="text-2xl font-runtime font-bold text-gray-900">Contact Us</h2>
            </div>
            <div className="space-y-4 text-gray-700 font-lucy-rose leading-relaxed">
              <p>
                Should you have any feedback, comments, requests for technical support or other inquiries, please contact us by email: hello@DurgaPuja.Network
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default Disclaimer;